/** @typedef {{title?: string, placement?: 'right' | 'left'}} TooltipOptions */
class CustomTooltip {
    /** @type {CustomTooltip[]} */
    static #instances = [];

    /**
     * @param {Element} domElement - The DOM element to get the instance from.
     * @returns {CustomTooltip | undefined} The tooltip's instance if found. Else undefined.
     */
    static getInstance = (domElement) =>
        CustomTooltip.#instances.find((instance) => instance.#tooltipTargetDom === domElement);

    static destroyAll = () => {
        CustomTooltip.#instances.forEach((instance) => {
            instance.dispose();
        });
    };

    /** @param {CustomTooltip} instance - The instance to remove. */
    static #removeInstance = (instance) => {
        CustomTooltip.#instances = CustomTooltip.#instances.filter((storedInstance) => storedInstance !== instance);
    };

    /** @param {CustomTooltip} instance - The instance to add. */
    static #addInstance = (instance) => {
        CustomTooltip.#instances.push(instance);
    };

    static #tooltipIndex = 0;

    /**
     * @param {Element} domElement - The DOM element to attach the tooltip to.
     * @param {TooltipOptions} options - Options customizing the tooltip.
     */
    constructor(domElement, options) {
        this.#tooltipTargetDom = domElement;
        this.#tooltipId = `custom-tooltip-${CustomTooltip.#tooltipIndex++}`;
        this.#tooltipDom = Object.assign(document.createElement('div'), {
            className: 'custom-tooltip',
            role: 'tooltip',
            style: 'transform: translateY(-50%);',
            id: this.#tooltipId,
        });

        this.#setOptions(options);
        this.#init();
    }

    #tooltipId;
    #tooltipDom;
    #tooltipTargetDom;
    /** @type {Required<TooltipOptions>} */
    #options = { title: '', placement: 'right' };
    #isVisible = false;

    #bindEvents = () => {
        this.#tooltipTargetDom.addEventListener('mouseenter', this.show);
        this.#tooltipTargetDom.addEventListener('mouseleave', this.hide);
        this.#tooltipTargetDom.addEventListener('focus', this.show);
        this.#tooltipTargetDom.addEventListener('blur', this.hide);
    };

    #unbindEvents = () => {
        this.#tooltipTargetDom.removeEventListener('mouseenter', this.show);
        this.#tooltipTargetDom.removeEventListener('mouseleave', this.hide);
        this.#tooltipTargetDom.removeEventListener('focus', this.show);
        this.#tooltipTargetDom.removeEventListener('blur', this.hide);
    };

    #init = () => {
        CustomTooltip.#addInstance(this);
        this.#bindEvents();
    };

    #render = () => {
        const tooltipDom = this.#tooltipDom;
        const tooltipTargetDom = this.#tooltipTargetDom;

        if (this.#isVisible) {
            const targetRect = tooltipTargetDom.getBoundingClientRect();
            const gapFromTooltip = 10;
            const { placement, title } = this.#options;

            tooltipDom.innerHTML = `
          <div class="custom-tooltip__arrow ${placement}"></div>
          <div class="custom-tooltip__inner">${title}</div>
        `;
            if (!document.body.contains(tooltipDom)) document.body.append(tooltipDom);

            const placementOffset =
                placement === 'right'
                    ? targetRect.right + gapFromTooltip
                    : targetRect.left - tooltipDom.getBoundingClientRect().width - gapFromTooltip;

            tooltipTargetDom.setAttribute('aria-describedby', this.#tooltipId);

            Object.assign(tooltipDom.style, {
                top: `${targetRect.top + targetRect.height / 2}px`,
                left: `${placementOffset}px`,
            });
        } else {
            tooltipTargetDom.removeAttribute('aria-describedby');
            tooltipDom.remove();
        }
    };

    #handleScroll = () => {
        if (this.#isVisible) this.#render();
    };

    show = () => {
        if (this.#isVisible) return;
        this.#isVisible = true;
        this.#render();
        window.addEventListener('scroll', this.#handleScroll, {
            capture: true,
        });
    };

    hide = () => {
        if (!this.#isVisible) return;
        this.#isVisible = false;
        this.#render();
        window.removeEventListener('scroll', this.#handleScroll, {
            capture: true,
        });
    };

    toggle = () => {
        if (this.#isVisible) {
            this.hide();
        } else {
            this.show();
        }
    };

    dispose = () => {
        this.hide();
        this.#unbindEvents();
        CustomTooltip.#removeInstance(this);
    };

    /** @param {TooltipOptions} root0 - The tooltip options. */
    #setOptions = ({ title = '', placement = 'right' }) => {
        this.#setTitle(title);
        this.#setPlacement(placement);
    };

    /** @param {'left' | 'right'} placement - Where to place the tooltip. */
    #setPlacement = (placement) => {
        const isPlacementSupported = ['left', 'right'].includes(placement);
        if (!isPlacementSupported) {
            console.warn('Unsupported tooltip placement given. Defaulting to right.');
        }

        this.#options.placement = isPlacementSupported ? placement : 'right';
    };

    /** @param {string} title - Title to show on tooltip. */
    #setTitle = (title) => {
        this.#options.title = title;
    };

    /** @param {'left' | 'right'} placement - Where to place the tooltip. */
    setPlacement = (placement) => {
        this.#setPlacement(placement);
        if (this.#isVisible) this.#render();
    };

    /** @param {string} title - Title to show on tooltip. */
    setTitle = (title) => {
        this.#setTitle(title);
        if (this.#isVisible) this.#render();
    };
}
export { CustomTooltip };
